/**
 * Cross-tab session store.
 *
 * Source of truth for the JWT session is localStorage (`sess_info`).
 * Changes are broadcast via the StorageEvent (other tabs) and BroadcastChannel
 * (faster + works when multiple listeners need structured messages).
 *
 * Also coordinates single-flight session refresh across tabs so only one tab
 * calls /check_session while others wait for the new token.
 */

export const SESS_INFO_KEY = 'sess_info';
export const SESSION_CHANNEL_NAME = 'molecommerce-session-v1';
export const SESS_REFRESH_LOCK_KEY = 'sess_refresh_lock';

/** Max age of a cross-tab refresh lock before it is considered stale. */
export const REFRESH_LOCK_TTL_MS = 12_000;

export type SessionSyncMessage =
	| { type: 'SESSION_SET'; session: any; originTabId: string; at: number }
	| { type: 'SESSION_CLEAR'; originTabId: string; at: number }
	| { type: 'SESSION_REFRESH_START'; originTabId: string; at: number; tokenTail: string }
	| { type: 'SESSION_REFRESH_DONE'; originTabId: string; at: number };

const TAB_ID_KEY = 'mole_tab_id';

function ensureTabId(): string {
	try {
		let id = sessionStorage.getItem(TAB_ID_KEY);
		if (!id) {
			id =
				typeof crypto !== 'undefined' && 'randomUUID' in crypto
					? crypto.randomUUID()
					: `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
			sessionStorage.setItem(TAB_ID_KEY, id);
		}
		return id;
	} catch {
		return `tab-${Date.now()}`;
	}
}

export const TAB_ID = ensureTabId();

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
	if (typeof BroadcastChannel === 'undefined') return null;
	if (!channel) {
		try {
			channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
		} catch {
			channel = null;
		}
	}
	return channel;
}

function postChannel(msg: SessionSyncMessage): void {
	try {
		getChannel()?.postMessage(msg);
	} catch {
		/* ignore */
	}
}

export function readStoredSession(): any | null {
	try {
		const raw = localStorage.getItem(SESS_INFO_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

export function writeStoredSession(
	session: any,
	options: { broadcast?: boolean } = {}
): void {
	const { broadcast = true } = options;
	const stamped = {
		...session,
		_syncAt: Date.now(),
	};
	localStorage.setItem(SESS_INFO_KEY, JSON.stringify(stamped));
	if (broadcast) {
		postChannel({
			type: 'SESSION_SET',
			session: stamped,
			originTabId: TAB_ID,
			at: stamped._syncAt,
		});
	}
}

export function clearStoredSession(
	options: { broadcast?: boolean } = {}
): void {
	const { broadcast = true } = options;
	localStorage.removeItem(SESS_INFO_KEY);
	if (broadcast) {
		postChannel({
			type: 'SESSION_CLEAR',
			originTabId: TAB_ID,
			at: Date.now(),
		});
	}
}

export function getSessionAccessToken(session: any | null | undefined): string | null {
	return session?.access_token || null;
}

export function getSessionUserId(session: any | null | undefined): number | null {
	const id = session?.user_id;
	return typeof id === 'number' ? id : id != null ? Number(id) : null;
}

export function sameAccessToken(a: any | null | undefined, b: any | null | undefined): boolean {
	const ta = getSessionAccessToken(a);
	const tb = getSessionAccessToken(b);
	return Boolean(ta && tb && ta === tb);
}

/** Prefer newer sync stamps when two tabs race. */
export function isSessionNewer(incoming: any, current: any | null): boolean {
	const inAt = Number(incoming?._syncAt) || 0;
	const curAt = Number(current?._syncAt) || 0;
	if (inAt && curAt) return inAt >= curAt;
	// No stamps: treat token change as newer.
	return !sameAccessToken(incoming, current);
}

// ---------------------------------------------------------------------------
// Cross-tab refresh lock (single-flight /check_session)
// ---------------------------------------------------------------------------

type RefreshLock = {
	tabId: string;
	startedAt: number;
	tokenTail: string;
};

function readRefreshLock(): RefreshLock | null {
	try {
		const raw = localStorage.getItem(SESS_REFRESH_LOCK_KEY);
		if (!raw) return null;
		return JSON.parse(raw) as RefreshLock;
	} catch {
		return null;
	}
}

function tokenTail(token: string): string {
	return token.slice(-16);
}

/**
 * Try to become the single tab that may call /check_session for this token.
 * Returns true if this tab holds the lock.
 */
export function tryAcquireRefreshLock(token: string): boolean {
	const now = Date.now();
	const existing = readRefreshLock();
	if (existing) {
		const fresh = now - existing.startedAt < REFRESH_LOCK_TTL_MS;
		if (fresh && existing.tabId !== TAB_ID) {
			return false;
		}
	}
	const lock: RefreshLock = {
		tabId: TAB_ID,
		startedAt: now,
		tokenTail: tokenTail(token),
	};
	try {
		localStorage.setItem(SESS_REFRESH_LOCK_KEY, JSON.stringify(lock));
	} catch {
		/* private mode — proceed single-tab */
	}
	postChannel({
		type: 'SESSION_REFRESH_START',
		originTabId: TAB_ID,
		at: now,
		tokenTail: lock.tokenTail,
	});
	return true;
}

export function releaseRefreshLock(): void {
	const existing = readRefreshLock();
	if (existing && existing.tabId !== TAB_ID) return;
	try {
		localStorage.removeItem(SESS_REFRESH_LOCK_KEY);
	} catch {
		/* ignore */
	}
	postChannel({
		type: 'SESSION_REFRESH_DONE',
		originTabId: TAB_ID,
		at: Date.now(),
	});
}

export function isRefreshLockedByOtherTab(): boolean {
	const existing = readRefreshLock();
	if (!existing) return false;
	if (existing.tabId === TAB_ID) return false;
	return Date.now() - existing.startedAt < REFRESH_LOCK_TTL_MS;
}

/** Wait until another tab finishes refresh or timeout; then return latest stored session. */
export async function waitForPeerSessionRefresh(
	previousToken: string,
	timeoutMs = 10_000
): Promise<any | null> {
	const started = Date.now();
	const prevTail = tokenTail(previousToken);

	return new Promise((resolve) => {
		const finish = (session: any | null) => {
			cleanup();
			resolve(session);
		};

		const tick = () => {
			const stored = readStoredSession();
			const storedToken = getSessionAccessToken(stored);
			if (storedToken && storedToken !== previousToken) {
				finish(stored);
				return;
			}
			// Lock gone and no new token yet — peer may have failed.
			if (!isRefreshLockedByOtherTab() && Date.now() - started > 400) {
				finish(stored);
				return;
			}
			if (Date.now() - started >= timeoutMs) {
				finish(readStoredSession());
			}
		};

		const onStorage = (event: StorageEvent) => {
			if (event.key === SESS_INFO_KEY || event.key === SESS_REFRESH_LOCK_KEY) {
				tick();
			}
		};

		const onMessage = (event: MessageEvent<SessionSyncMessage>) => {
			const data = event.data;
			if (!data || typeof data !== 'object') return;
			if (data.type === 'SESSION_SET' || data.type === 'SESSION_REFRESH_DONE') {
				tick();
			}
			if (data.type === 'SESSION_REFRESH_START' && data.tokenTail === prevTail) {
				// Peer started; keep waiting.
			}
		};

		const interval = window.setInterval(tick, 150);
		window.addEventListener('storage', onStorage);
		const ch = getChannel();
		ch?.addEventListener('message', onMessage);

		const cleanup = () => {
			window.clearInterval(interval);
			window.removeEventListener('storage', onStorage);
			ch?.removeEventListener('message', onMessage);
		};

		tick();
	});
}

export type SessionSyncHandler = (message: SessionSyncMessage) => void;

/**
 * Subscribe to cross-tab session changes.
 * Returns an unsubscribe function.
 */
export function subscribeSessionSync(handler: SessionSyncHandler): () => void {
	const onStorage = (event: StorageEvent) => {
		if (event.key !== SESS_INFO_KEY) return;
		if (event.newValue == null) {
			handler({
				type: 'SESSION_CLEAR',
				originTabId: 'storage',
				at: Date.now(),
			});
			return;
		}
		try {
			const session = JSON.parse(event.newValue);
			handler({
				type: 'SESSION_SET',
				session,
				originTabId: 'storage',
				at: Number(session?._syncAt) || Date.now(),
			});
		} catch {
			/* ignore corrupt payload */
		}
	};

	const onMessage = (event: MessageEvent<SessionSyncMessage>) => {
		const data = event.data;
		if (!data || typeof data !== 'object') return;
		if (data.originTabId === TAB_ID) return; // ignore own echoes
		if (
			data.type !== 'SESSION_SET' &&
			data.type !== 'SESSION_CLEAR' &&
			data.type !== 'SESSION_REFRESH_START' &&
			data.type !== 'SESSION_REFRESH_DONE'
		) {
			return;
		}
		handler(data);
	};

	window.addEventListener('storage', onStorage);
	const ch = getChannel();
	ch?.addEventListener('message', onMessage);

	return () => {
		window.removeEventListener('storage', onStorage);
		ch?.removeEventListener('message', onMessage);
	};
}
